import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import Select from "react-select";
import { Badge, Button, Card, DescriptionList, Disclosure, Field, Table } from '../../../../ui';
import type { BadgeTone } from '../../../../ui';
import { Actions, FormGrid, Tabs } from './ui-pending';
import { dsReactSelectStyles } from './ui-pending/ReactSelectStyles';
import { Link } from 'react-router-dom';
import {
  FindTeacherStudentV2,
  aeForm,
  previewSchedule,
  postTeacherAssignmentAlert,
  getOnboardingTimeBucketsV3,
  getOnboardingTeachersV3,
  getOnboardingSchedulePreviewV3,
  studentCourses,
  type OnboardingTimeBucket,
} from '../../../../Api/apiUtils';
import { ConfigContext } from '../../../../Api/global/ApiContext';
import { toast } from 'react-toastify';
import "./global.scss";
import { useNavigate } from "react-router-dom";
import { PaymentConfigContext } from '../../../../Api/global/ApiContext';
import { MixpanelEvents, trackEvent } from '../../../../utils/mixpanelTracking';
import BookingPreview, { type PreviewScheduleResponse } from './BookingPreview';

interface FindTeacherProps {
  studentId: any;
  profileData?: any;
  onTeacherSelect?: (teacher: TableDataItem) => void;
  onBookAllClasses?: (teacher: TableDataItem) => void | Promise<void>;
  preselectedCourseId?: string;
  disableCourseSelect?: boolean;
  fetchCourses?: () => Promise<void> | void;
  /** Only show Book All Classes on the student Find Teacher tab, not payment/teacher-change modals */
  showBookAllClasses?: boolean;
  onTeachersLoaded?: (teachers: TableDataItem[]) => void;
}

interface TableDataItem {
  firstName: string;
  lastName: string;
  paidStudents:any;
  payoutCategory: string;
  allocationPriority: string;
  profilePhoto: string;
  activeStudentCount: number;
  paidStudentCount: number;
  teacherId:any;
  credentialId:any;
  activeStudents:any;
  missedCount: number;
  userDetails: any;
  timezone:any;
  id: any;
  selectedTime:any;
  selectedDay:any;
  teacherName?: string;
  teacherUserId?: string | number;
  userId?: string | number;
  /** Onboarding v3: open times grouped by day. */
  availability?: Array<{ day: string; times: string[] }>;
  matchedSlots?: Array<{ day: string; time: string }>;
}

const TEACHER_CAPACITY_LIMIT = 70;
const PAYOUT_TIER_ORDER = ["SILVER", "GOLD", "PLATINUM", "DIAMOND"];
const TIER_TONES: Record<string, BadgeTone> = {
  SILVER: "success",
  GOLD: "warning",
  PLATINUM: "primary",
  DIAMOND: "info",
};
const selectStyles = dsReactSelectStyles<any>();
/** Render menus in a portal so modal overflow doesn't clip / push the dialog. */
const selectMenuProps = {
  styles: selectStyles,
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed" as const,
};

const getTeacherActiveStudents = (teacher: TableDataItem): number => {
  const value = Number(teacher.activeStudents ?? teacher.activeStudentCount ?? 0);
  return Number.isFinite(value) ? value : 0;
};

const isTeacherAtCapacity = (teacher: TableDataItem): boolean =>
  getTeacherActiveStudents(teacher) > TEACHER_CAPACITY_LIMIT;

/** "08:00:00" / "08:00" → "8:00 AM" */
const formatSlotTime = (time?: string): string => {
  if (!time) return "";
  const [hStr, mStr] = String(time).split(":");
  let hour = parseInt(hStr, 10);
  if (Number.isNaN(hour)) return String(time);
  const minutes = (mStr || "00").slice(0, 2);
  const meridiem = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minutes} ${meridiem}`;
};

const formatDayShort = (day?: string): string => {
  if (!day) return "";
  const d = String(day).trim();
  return d.charAt(0).toUpperCase() + d.slice(1, 3).toLowerCase();
};

type DayTimeSelection = Record<string, string>; // day -> time

const getTeacherAvailabilityRows = (
  teacher: TableDataItem
): Array<{ day: string; times: string[] }> => {
  if (Array.isArray(teacher.availability) && teacher.availability.length) {
    return teacher.availability
      .map((entry) => ({
        day: String(entry.day || "").toUpperCase(),
        times: (entry.times || []).map(String).filter(Boolean),
      }))
      .filter((entry) => entry.day && entry.times.length);
  }
  if (teacher.selectedDay && teacher.selectedTime) {
    return [
      {
        day: String(teacher.selectedDay).toUpperCase(),
        times: [String(teacher.selectedTime)],
      },
    ];
  }
  return [];
};

const TeacherAvailabilityCell: React.FC<{
  teacher: TableDataItem;
  selectable?: boolean;
  selectedByDay?: DayTimeSelection;
  requiredCount?: number;
  disabled?: boolean;
  onToggleTime?: (day: string, time: string) => void;
}> = ({
  teacher,
  selectable = false,
  selectedByDay = {},
  requiredCount = 0,
  disabled = false,
  onToggleTime,
}) => {
  const rows = getTeacherAvailabilityRows(teacher);
  const selectedCount = Object.keys(selectedByDay).length;

  if (!rows.length) {
    return <span className="ds-text-subtle">—</span>;
  }

  return (
    <div className="find-teacher__slots">
      {selectable && (
        <span className="find-teacher__slots-hint">
          {selectedCount >= requiredCount
            ? "Times selected — ready to book"
            : `Pick ${requiredCount} time${requiredCount === 1 ? "" : "s"} (one per day)`}
        </span>
      )}
      {rows.map((entry) => (
        <div key={entry.day} className="find-teacher__slot-day">
          <span className="find-teacher__slot-day-label">{formatDayShort(entry.day)}</span>
          <span className="find-teacher__slot-times">
            {entry.times.map((time) => {
              const isSelected = selectedByDay[entry.day] === time;
              if (!selectable) {
                return (
                  <Badge key={`${entry.day}-${time}`} tone="neutral">
                    {formatSlotTime(time)}
                  </Badge>
                );
              }
              return (
                <button
                  key={`${entry.day}-${time}`}
                  type="button"
                  className={`find-teacher__slot-chip${isSelected ? " is-selected" : ""}`}
                  disabled={disabled}
                  aria-pressed={isSelected}
                  onClick={() => onToggleTime?.(entry.day, time)}
                >
                  {formatSlotTime(time)}
                </button>
              );
            })}
          </span>
        </div>
      ))}
    </div>
  );
};

export const getTeacherPayoutTier = (category?: string): string =>
  String(category || "").trim().toUpperCase();

export const hasAvailableSilverTeachers = (
  teachers: Array<{
    payoutCategory?: string;
    activeStudents?: string | number;
    activeStudentCount?: string | number;
  }>
): boolean =>
  teachers.some((teacher) => {
    if (getTeacherPayoutTier(teacher.payoutCategory) !== "SILVER") return false;
    return getTeacherActiveStudents(teacher as TableDataItem) <= TEACHER_CAPACITY_LIMIT;
  });

export const maybePostTeacherAssignmentAlert = async ({
  enrollmentId,
  teacherId,
  assignedPayoutCategory,
  availableTeachers,
}: {
  enrollmentId?: string | number | null;
  teacherId?: string | number | null;
  assignedPayoutCategory?: string;
  availableTeachers: Array<{
    payoutCategory?: string;
    activeStudents?: string | number;
    activeStudentCount?: string | number;
  }>;
}): Promise<void> => {
  if (enrollmentId == null || String(enrollmentId).trim() === "") return;
  if (teacherId == null || String(teacherId).trim() === "") return;

  const assignedTier = getTeacherPayoutTier(assignedPayoutCategory);
  if (!assignedTier || assignedTier === "SILVER") return;
  if (!hasAvailableSilverTeachers(availableTeachers)) return;

  try {
    await postTeacherAssignmentAlert(enrollmentId, teacherId);
  } catch (error) {
    console.error("Teacher assignment alert failed:", error);
  }
};

const FindTeacher: React.FC<FindTeacherProps> = ({
  studentId,
  profileData,
  onTeacherSelect,
  onBookAllClasses,
  preselectedCourseId,
  disableCourseSelect,
  fetchCourses,
  showBookAllClasses = false,
  onTeachersLoaded,
}) => {

  const [tableData, setTableData] = useState<TableDataItem[]>([]);  
  const [expandedTier, setExpandedTier] = useState<string>("");
  /** Per-teacher day → time picks used for Book All Classes (onboarding v3). */
  const [slotSelections, setSlotSelections] = useState<Record<string, DayTimeSelection>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [bookingTeacherId, setBookingTeacherId] = useState<string | number | null>(null);
  const [lastSearchPayload, setLastSearchPayload] = useState<any>(null);
  const [previewTeacher, setPreviewTeacher] = useState<TableDataItem | null>(null);
  const [previewData, setPreviewData] = useState<PreviewScheduleResponse | null>(null);
  const [previewMatchedSlots, setPreviewMatchedSlots] = useState<{ day: string; time: string }[]>([]);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [isKaraokeSelected, setIsKaraokeSelected] = useState(false);
  const [aeList, setAeList] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const paymentConfig = useContext(PaymentConfigContext);

  useEffect(() => {
    if (tableData.length > 0) {
      onTeachersLoaded?.(tableData);
    }
  }, [tableData, onTeachersLoaded]);

  const groupedTeacherData = useMemo(() => {
    const groups = new Map<string, TableDataItem[]>();
    tableData.forEach((teacher) => {
      const tier = String(teacher.payoutCategory || "OTHER").trim().toUpperCase();
      if (!groups.has(tier)) groups.set(tier, []);
      groups.get(tier)!.push(teacher);
    });

    return Array.from(groups.entries())
      .sort(([tierA], [tierB]) => {
        const indexA = PAYOUT_TIER_ORDER.indexOf(tierA);
        const indexB = PAYOUT_TIER_ORDER.indexOf(tierB);
        if (indexA === -1 && indexB === -1) return tierA.localeCompare(tierB);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      })
      .map(([tier, teachers]) => ({
        tier,
        teachers: [...teachers].sort((a, b) => {
          const priorityA = a.allocationPriority === "1" ? 0 : 1;
          const priorityB = b.allocationPriority === "1" ? 0 : 1;
          return priorityA - priorityB;
        }),
      }));
  }, [tableData]);
  const rawOnlineCourseAcademic2 = paymentConfig?.onlineCourseAcademic2;
  const onlineCourseAcademic2: Array<{
    name: string;
    paidCourseId: string;
    demoCourseId?: string;
    levels?: Array<{ name: string; classsize?: string; levelId: string }>;
  }> = Array.isArray(rawOnlineCourseAcademic2) ? rawOnlineCourseAcademic2 : [];

  const academic2CourseOptions = useMemo(
    () =>
      onlineCourseAcademic2
        .map((c) => ({
          value: String(c?.paidCourseId ?? "").trim(),
          label: c?.name ?? "",
          originalData: c,
        }))
        .filter((opt) => opt.value !== ""),
    [onlineCourseAcademic2],
  );

  const navigate = useNavigate();

  const config = useContext(ConfigContext);
  const { ageGroups = [], languages = [] } = config || {};

  const ageGroupOptions = ageGroups.map((ageGroup: any) => ({
    value: ageGroup.id.toString(),
    label: ageGroup.name,
  }));

  const languageOptions = languages.map((language: any) => ({
    value: language.id.toString(),
    label: language.name,
  }));

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];

  const selectedGender = genderOptions.find(opt => opt.value === profileData?.gender);
  const selectedLanguage = languageOptions.find((opt:any) => 
    opt.value === profileData?.languageId?.[0]?.id.toString()
  );
  const selectedAgeGroup = ageGroupOptions.find((opt:any) => 
    opt.label === profileData?.ageGroup || opt.range === profileData?.ageGroup
  );

  useEffect(() => {
    fetchAeDetails();
  }, [studentId]);

  const fetchAeDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await aeForm(studentId);
      if (response && response.length > 0) {
        const rId = response[0]?.recommendedPaidCourseId;
        if (rId != null && rId !== "") {
          const idStr = String(rId).trim();
          const course = onlineCourseAcademic2.find((c) => String(c?.paidCourseId ?? "").trim() === idStr);
          if (course) {
            response[0]._recommendedCourseName = course.name;
            response[0]._recommendedCourseLevel = course.levels?.[0]?.name ?? "";
          }
        }
        setAeList(response[0]);
      } else {
        setError("No assessment data available");
      }
    } catch (error) {
      console.error("Error fetching AE details:", error);
      setError("Failed to fetch AE form data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  const [formData, setFormData] = useState({
    studentId: studentId,
    selectedTime1: "",
    selectedTime2: "",
    courseId: "",
    selectedDay1: "",
    selectedDay2: "",
    ageGroupId: selectedAgeGroup?.value || "",
    studentLanguageId: selectedLanguage?.value || "",
    sgender: selectedGender?.value || "",
    level: selectedAgeGroup?.value === "karaoke-related-value" ? 'Preparatory' : "",
    selectWeeksCount: "",
    selectDaysCount: "",
    /** Kept for booking payload; inferred as fixed when booking from admin. */
    scheduleType: "fixed" as "" | "flexible" | "fixed",
    classesPerWeek: "" as "" | "1" | "2",
    /** Onboarding v3 day-part bucket id (morning / afternoon / …). */
    timeBucketId: "",
  });
  const [timeBuckets, setTimeBuckets] = useState<OnboardingTimeBucket[]>([]);
  const [bucketsLoading, setBucketsLoading] = useState(false);
  const [enrollmentId, setEnrollmentId] = useState<string | number | null>(null);

  const [selectedCourse, setSelectedCourse] = useState<string>(preselectedCourseId || "");

  useEffect(() => {
    if (preselectedCourseId) {
      setSelectedCourse(preselectedCourseId);
      setFormData(prev => ({ ...prev, courseId: preselectedCourseId }));
    }
  }, [preselectedCourseId]);

  useEffect(() => {
    if (tableData.length > 0) {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [tableData]);

  useEffect(() => {
    setExpandedTier(groupedTeacherData[0]?.tier || "");
  }, [groupedTeacherData]);

  useEffect(() => {
    if (previewTeacher && previewData) {
      previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [previewTeacher, previewData]);

  const customTime = [
    { value: "00:00:00", label: "00:00 AM" },
    { value: "00:30:00", label: "00:30 AM" },
    { value: "01:00:00", label: "01:00 AM" },
    { value: "01:30:00", label: "01:30 AM" },
    { value: "02:00:00", label: "02:00 AM" },
    { value: "02:30:00", label: "02:30 AM" },
    { value: "03:00:00", label: "03:00 AM" },
    { value: "03:30:00", label: "03:30 AM" },
    { value: "04:00:00", label: "04:00 AM" },
    { value: "04:30:00", label: "04:30 AM" },
    { value: "05:00:00", label: "05:00 AM" },
    { value: "05:30:00", label: "05:30 AM" },
    { value: "06:00:00", label: "06:00 AM" },
    { value: "06:30:00", label: "06:30 AM" },
    { value: "07:00:00", label: "07:00 AM" },
    { value: "07:30:00", label: "07:30 AM" },
    { value: "08:00:00", label: "08:00 AM" },
    { value: "08:30:00", label: "08:30 AM" },
    { value: "09:00:00", label: "09:00 AM" },
    { value: "09:30:00", label: "09:30 AM" },
    { value: "10:00:00", label: "10:00 AM" },
    { value: "10:30:00", label: "10:30 AM" },
    { value: "11:00:00", label: "11:00 AM" },
    { value: "11:30:00", label: "11:30 AM" },
    { value: "12:00:00", label: "12:00 PM" },
    { value: "12:30:00", label: "12:30 PM" },
    { value: "13:00:00", label: "01:00 PM" },
    { value: "13:30:00", label: "01:30 PM" },
    { value: "14:00:00", label: "02:00 PM" },
    { value: "14:30:00", label: "02:30 PM" },
    { value: "15:00:00", label: "03:00 PM" },
    { value: "15:30:00", label: "03:30 PM" },
    { value: "16:00:00", label: "04:00 PM" },
    { value: "16:30:00", label: "04:30 PM" },
    { value: "17:00:00", label: "05:00 PM" },
    { value: "17:30:00", label: "05:30 PM" },
    { value: "18:00:00", label: "06:00 PM" },
    { value: "18:30:00", label: "06:30 PM" },
    { value: "19:00:00", label: "07:00 PM" },
    { value: "19:30:00", label: "07:30 PM" },
    { value: "20:00:00", label: "08:00 PM" },
    { value: "20:30:00", label: "08:30 PM" },
    { value: "21:00:00", label: "09:00 PM" },
    { value: "21:30:00", label: "09:30 PM" },
    { value: "22:00:00", label: "10:00 PM" },
    { value: "22:30:00", label: "10:30 PM" },
    { value: "23:00:00", label: "11:00 PM" },
    { value: "23:30:00", label: "11:30 PM" },
  ];

  const dayOptions = [
    { value: "MONDAY", label: "Monday" },
    { value: "TUESDAY", label: "Tuesday" },
    { value: "WEDNESDAY", label: "Wednesday" },
    { value: "THURSDAY", label: "Thursday" },
    { value: "FRIDAY", label: "Friday" },
    { value: "SATURDAY", label: "Saturday" },
    { value: "SUNDAY", label: "Sunday" },
  ];

  const defaultLevelOptions = [
    { value: 'Preparatory', label: 'Preparatory' },
    { value: 'Intermediate', label: 'Intermediate' },
    { value: 'Proficient', label: 'Proficient' },
    { value: 'Advanced', label: 'Advanced' },
  ];

  /** Levels for the paid course selection (falls back to defaults if mapping has none). */
  const levelOptions = useMemo(() => {
    const id = String(formData.courseId || selectedCourse || "").trim();
    const course = onlineCourseAcademic2.find((c) => String(c?.paidCourseId ?? "").trim() === id);
    if (course?.levels?.length) {
      return course.levels.map((l) => ({ value: l.name, label: l.name }));
    }
    return defaultLevelOptions;
  }, [onlineCourseAcademic2, formData.courseId, selectedCourse]);

  const weeksCountOptions = [
    { value: "1", label: "1" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4", label: "4" },
    { value: "5", label: "5" },
    { value: "6", label: "6" },
    { value: "7", label: "7" },
    { value: "8", label: "8" },
    { value: "9", label: "9" },
    { value: "10", label: "10" },
  ];

 

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const preferredDayCount = formData.classesPerWeek === "1" ? 1 : formData.classesPerWeek === "2" ? 2 : 0;

  const selectedDaysList = (): string[] => {
    const days = [formData.selectedDay1, formData.selectedDay2]
      .map((d) => String(d || "").trim().toUpperCase())
      .filter(Boolean);
    return preferredDayCount === 1 ? days.slice(0, 1) : days.slice(0, 2);
  };

  const isFormValid = () => {
    if (!formData.level || !formData.courseId || !formData.classesPerWeek) return false;
    if (!formData.selectDaysCount) return false;
    if (!formData.selectedDay1) return false;
    if (preferredDayCount === 2 && !formData.selectedDay2) return false;
    if (!formData.timeBucketId) return false;
    return true;
  };

  const resolveEnrollmentForCourse = async (courseId: string | number) => {
    try {
      const courses = await studentCourses(studentId);
      const list = Array.isArray(courses) ? courses : [];
      const match = list.find(
        (c: any) =>
          String(c?.courseId ?? c?.paidCourseId ?? "") === String(courseId) && !c?.isDemoCourse,
      );
      return (
        match?.courseEnrollmentId ??
        match?.enrollmentId ??
        list.find((c: any) => String(c?.courseId) === String(courseId))?.courseEnrollmentId ??
        null
      );
    } catch (error) {
      console.error("Failed to resolve enrollmentId:", error);
      return null;
    }
  };

  // Load day-part buckets (morning / afternoon / …) once days + course + start date are ready.
  useEffect(() => {
    let cancelled = false;
    const days = selectedDaysList();
    const needDays = preferredDayCount;
    if (!formData.courseId || !needDays || days.length < needDays || !formData.selectDaysCount) {
      setTimeBuckets([]);
      setFormData((prev) => (prev.timeBucketId ? { ...prev, timeBucketId: "" } : prev));
      return;
    }

    (async () => {
      setBucketsLoading(true);
      try {
        const eid = await resolveEnrollmentForCourse(formData.courseId);
        if (cancelled) return;
        setEnrollmentId(eid);
        if (eid == null || String(eid).trim() === "") {
          setTimeBuckets([]);
          toast.warning("No enrollment found for this course — cannot load time-of-day options.");
          return;
        }
        const data = await getOnboardingTimeBucketsV3({
          enrollmentId: eid,
          selectedDays: days,
          startDate: buildStartDate(),
        });
        if (cancelled) return;
        if (data?.matchType === "suggested" && data?.matchedRung?.days?.length) {
          const suggested = data.matchedRung.days.map((d) => String(d).toUpperCase());
          setFormData((prev) => ({
            ...prev,
            selectedDay1: suggested[0] || prev.selectedDay1,
            selectedDay2: suggested[1] || (preferredDayCount === 2 ? prev.selectedDay2 : ""),
            timeBucketId: "",
          }));
          toast.info("Preferred days were adjusted to available teacher days.");
        }
        const buckets = Array.isArray(data?.buckets) ? data.buckets : [];
        setTimeBuckets(buckets);
        setFormData((prev) => {
          if (prev.timeBucketId && buckets.some((b) => String(b.id) === String(prev.timeBucketId))) {
            return prev;
          }
          const firstAvailable = buckets.find((b) => String(b.status || "").toUpperCase() === "AVAILABLE");
          return { ...prev, timeBucketId: firstAvailable ? String(firstAvailable.id) : "" };
        });
        if (!buckets.length) {
          setTableData([]);
          toast.error("No Teacher Found");
        }
      } catch (error) {
        console.error("Failed to load time buckets:", error);
        if (!cancelled) {
          setTimeBuckets([]);
          toast.error("Failed to load preferred time-of-day options.");
        }
      } finally {
        if (!cancelled) setBucketsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuild when prefs that feed buckets change
  }, [
    formData.courseId,
    formData.classesPerWeek,
    formData.selectedDay1,
    formData.selectedDay2,
    formData.selectDaysCount,
  ]);

  const handleCourseChange = (selectedOption: any) => {
    const courseName = selectedOption?.originalData?.name ?? "";
    const isKaraoke =
      typeof courseName === "string" && courseName.toLowerCase().includes("karaoke");
    setIsKaraokeSelected(isKaraoke);

    const levels = selectedOption?.originalData?.levels as Array<{ name: string }> | undefined;
    setFormData((prev) => {
      let nextLevel = prev.level;
      if (isKaraoke) {
        nextLevel = "Preparatory";
      } else if (levels?.length) {
        nextLevel = levels.some((l) => l.name === prev.level) ? prev.level : levels[0].name;
      }
      return {
        ...prev,
        courseId: selectedOption?.value,
        level: nextLevel,
      };
    });
  };

  const buildSearchPayload = () => {
    const selectedDays = selectedDaysList();
    return {
      studentId: formData.studentId,
      studentTimezone: profileData?.timezone,
      scheduleType: "fixed",
      classesPerWeek: formData.classesPerWeek,
      selectedDays,
      timeBucketId: formData.timeBucketId,
      courseId: formData.courseId,
      ageGroupId: formData.ageGroupId || "",
      sgender: formData.sgender || "",
      studentLanguageId: formData.studentLanguageId || "",
      level: formData.level,
      startDate: buildStartDate(),
      enrollmentId,
    };
  };

  const teacherSelectionKey = (teacher: TableDataItem): string =>
    String(teacher.teacherId ?? teacher.id ?? teacher.credentialId ?? "");

  const getManualSelections = (
    teacher: TableDataItem,
  ): { day: string; time: string }[] => {
    const map = slotSelections[teacherSelectionKey(teacher)] || {};
    return Object.entries(map)
      .filter(([, time]) => Boolean(time))
      .map(([day, time]) => ({ day: String(day).toUpperCase(), time: String(time) }));
  };

  const toggleTeacherSlot = (teacher: TableDataItem, day: string, time: string) => {
    const key = teacherSelectionKey(teacher);
    const dayKey = String(day).toUpperCase();
    setSlotSelections((prev) => {
      // Only one teacher can have slot picks at a time — clear others.
      const current: DayTimeSelection = { ...(prev[key] || {}) };
      if (current[dayKey] === time) {
        delete current[dayKey];
      } else {
        current[dayKey] = time;
        const days = Object.keys(current);
        const max = preferredDayCount || days.length;
        if (max > 0 && days.length > max) {
          days
            .filter((d) => d !== dayKey)
            .slice(0, days.length - max)
            .forEach((d) => {
              delete current[d];
            });
        }
      }
      if (!Object.keys(current).length) return {};
      return { [key]: current };
    });
  };

  /** Manual picks first; otherwise earliest open time on each preferred day. */
  const buildSelectionsFromTeacher = (
    teacher: TableDataItem,
  ): { day: string; time: string }[] => {
    const manual = getManualSelections(teacher);
    if (manual.length) return manual;

    const days = selectedDaysList();
    const availability = Array.isArray(teacher.availability) ? teacher.availability : [];
    const selections: { day: string; time: string }[] = [];
    days.forEach((day) => {
      const entry = availability.find((a) => String(a.day).toUpperCase() === day);
      const time =
        entry?.times?.[0] ||
        teacher.matchedSlots?.find((s) => String(s.day).toUpperCase() === day)?.time;
      if (time) selections.push({ day, time: String(time) });
    });
    return selections;
  };

  const mapV3Teacher = (t: any): TableDataItem => ({
    firstName: t?.firstName || "",
    lastName: t?.lastName || "",
    paidStudents: t?.paidStudents ?? t?.paidStudentCount,
    payoutCategory: t?.payoutCategory || "",
    allocationPriority: t?.allocationPriority || "",
    profilePhoto: t?.profilePhotoUrl || t?.profilePhoto || "",
    activeStudentCount: Number(t?.activeStudentCount ?? t?.activeStudents ?? 0),
    paidStudentCount: Number(t?.paidStudentCount ?? t?.paidStudents ?? 0),
    teacherId: t?.teacherId ?? t?.id,
    credentialId: t?.credentialId ?? t?.teacherUserId ?? t?.userId,
    activeStudents: t?.activeStudents ?? t?.activeStudentCount,
    missedCount: Number(t?.missedCount ?? 0),
    userDetails: t?.userDetails,
    timezone: t?.timezone,
    id: t?.id ?? t?.teacherId,
    selectedTime: t?.selectedTime,
    selectedDay: t?.selectedDay,
    teacherName: [t?.firstName, t?.lastName].filter(Boolean).join(" "),
    teacherUserId: t?.teacherUserId ?? t?.userId,
    userId: t?.userId ?? t?.teacherUserId,
    availability: t?.availability,
    matchedSlots: t?.matchedSlots,
  });

  const buildStartDate = (): string => {
    const daysAhead = parseInt(String(formData.selectDaysCount || "0"), 10);
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    if (!Number.isNaN(daysAhead) && daysAhead > 0) {
      date.setDate(date.getDate() + daysAhead);
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const buildMatchedSlots = (
    teacher: TableDataItem
  ): { day: string; time: string; timeZone: string }[] => {
    const fromSearch = (lastSearchPayload?.selectedClasses || [])
      .filter((c: any) => c?.day && c?.time)
      .map((c: any) => ({
        day: String(c.day).toUpperCase(),
        time: String(c.time),
        timeZone: "IST",
      }));

    if (fromSearch.length > 0) return fromSearch;

    if (teacher.selectedDay && teacher.selectedTime) {
      return [
        {
          day: String(teacher.selectedDay).toUpperCase(),
          time: String(teacher.selectedTime),
          timeZone: "IST",
        },
      ];
    }
    return [];
  };

  const handleBookAllClasses = async (teacher: TableDataItem) => {
    if (isTeacherAtCapacity(teacher)) {
      toast.error(
        `This teacher is at capacity with ${getTeacherActiveStudents(teacher)} active students.`
      );
      return;
    }
    if (!lastSearchPayload) {
      toast.error("Please search for teachers before booking classes.");
      return;
    }

    const need = preferredDayCount || 1;
    const manual = getManualSelections(teacher);
    if (manual.length < need) {
      toast.warning(
        `Select ${need} class time${need === 1 ? "" : "s"} (one per day) before booking.`
      );
      return;
    }

    const matchedSlots = manual;

    if (!matchedSlots.length) {
      toast.error("No available class times for this teacher on the preferred days.");
      return;
    }

    const teacherUserId =
      teacher.teacherUserId ??
      teacher.userId ??
      teacher.credentialId;

    if (teacherUserId == null || teacherUserId === "") {
      toast.error("Teacher user id is missing.");
      return;
    }

    const teacherKey = teacher.teacherId ?? teacher.id;
    setBookingTeacherId(teacherKey);

    try {
      if (onBookAllClasses) {
        await onBookAllClasses(teacher);
        return;
      }

      const eid =
        enrollmentId ??
        lastSearchPayload?.enrollmentId ??
        (await resolveEnrollmentForCourse(formData.courseId));
      if (eid == null || String(eid).trim() === "") {
        toast.error("No enrollment found for this course.");
        return;
      }

      const data = await getOnboardingSchedulePreviewV3({
        enrollmentId: eid,
        teacherUserId: String(teacherUserId),
        startDate: buildStartDate(),
        selections: matchedSlots,
      });
      setPreviewTeacher(teacher);
      setPreviewMatchedSlots(matchedSlots);
      setPreviewData(data || null);
    } catch (error: any) {
      console.error("Error fetching schedule preview:", error);
      // Fall back to legacy preview if v3 fails (older enrollments).
      try {
        const data = await previewSchedule({
          studentId: Number(formData.studentId) || formData.studentId,
          teacherUserId: String(teacherUserId),
          courseId: String(formData.courseId),
          scheduleType: "fixed",
          matchedSlots: matchedSlots.map((s) => ({ ...s, timeZone: "IST" })),
          startDate: buildStartDate(),
        });
        setPreviewTeacher(teacher);
        setPreviewMatchedSlots(matchedSlots);
        setPreviewData(data || null);
      } catch (legacyError: any) {
        const message =
          error?.response?.data?.message ||
          legacyError?.response?.data?.message ||
          (typeof error?.response?.data === "string" ? error.response.data : "") ||
          "Failed to load booking preview. Please try again.";
        toast.error(message);
      }
    } finally {
      setBookingTeacherId(null);
    }
  };

  const closeBookingPreview = () => {
    setPreviewTeacher(null);
    setPreviewData(null);
    setPreviewMatchedSlots([]);
  };

  const handleSubmit = async () => {
    const selectedCourseOption = academic2CourseOptions.find((opt) => opt.value === formData.courseId);
    const courseName = selectedCourseOption?.label || "";
    const payload = buildSearchPayload();
    const days = selectedDaysList();

    if (!isFormValid()) {
      toast.warning("Please complete classes per week, preferred days, and time of day.");
      return;
    }

    try {
      setIsLoading(true);
      const eid =
        enrollmentId ??
        (await resolveEnrollmentForCourse(formData.courseId));
      if (eid == null || String(eid).trim() === "") {
        toast.error("No enrollment found for this course.");
        setIsLoading(false);
        return;
      }
      setEnrollmentId(eid);

      const data = await getOnboardingTeachersV3({
        enrollmentId: eid,
        bucket: formData.timeBucketId,
        selectedDays: days,
        startDate: buildStartDate(),
      });
      const teachers = (Array.isArray(data?.teachers) ? data.teachers : []).map(mapV3Teacher);

      trackEvent(MixpanelEvents.FIND_TEACHER_SEARCH, {
        studentId: formData.studentId,
        courseId: formData.courseId,
        courseName,
        level: formData.level,
        selectedDay1: formData.selectedDay1,
        selectedDay2: formData.selectedDay2,
        timeBucketId: formData.timeBucketId,
        classesPerWeek: formData.classesPerWeek,
        searchType: "v3",
        studentTimezone: profileData?.timezone,
        resultStatus: teachers.length ? "success" : "no_data",
        teachersFound: teachers.length,
      });

      setSlotSelections({});
      setTableData(teachers);
      setLastSearchPayload({ ...payload, enrollmentId: eid });
      if (!teachers.length) toast.error("No Teacher Found");
    } catch (error) {
      console.error("Error submitting form:", error);
      // Fall back to legacy admin find-teacher if v3 is unavailable.
      try {
        const legacy = await FindTeacherStudentV2({
          studentId: formData.studentId,
          studentTimezone: profileData?.timezone,
          scheduleType: "fixed",
          selectedClasses: days.map((day) => ({ day, time: "10:00:00" })),
          alternateClasses: [],
          courseId: formData.courseId,
          ageGroupId: formData.ageGroupId || "",
          sgender: formData.sgender || "",
          studentLanguageId: formData.studentLanguageId || "",
          level: formData.level,
          teacherId: null,
          selectDaysCount: formData.selectDaysCount || undefined,
        });
        const list = Array.isArray(legacy?.data) ? legacy.data : [];
        setSlotSelections({});
        setTableData(list);
        setLastSearchPayload(payload);
        if (!list.length) toast.error("No Teacher Found");
      } catch (legacyError) {
        console.error("Legacy find teacher failed:", legacyError);
        toast.error("Failed to find teachers. Please try again.");
        setSlotSelections({});
        setTableData([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const showSearchFields = Boolean(formData.classesPerWeek);

  return (
    <div className="find-teacher">
      {previewTeacher && previewData ? (
        <div ref={previewRef}>
          <BookingPreview
            teacher={previewTeacher}
            matchedSlots={previewMatchedSlots}
            preview={previewData}
            studentId={studentId}
            studentUserId={
              profileData?.uId ||
              profileData?.userId ||
              profileData?.studentCredential ||
              profileData?.credentialId ||
              studentId
            }
            courseId={formData.courseId}
            enrollmentId={enrollmentId}
            scheduleType="fixed"
            fetchCourses={fetchCourses}
            availableTeachersForAlert={tableData}
            onBack={closeBookingPreview}
            onAssignTeacherOnly={() => {
              if (onTeacherSelect) {
                onTeacherSelect(previewTeacher);
              }
              closeBookingPreview();
            }}
            onBookSuccess={() => {
              closeBookingPreview();
              setTableData([]);
            }}
          />
        </div>
      ) : (
      <>
      {aeList && aeList.feedbacks && aeList.feedbacks
        .filter((item: any) => item.question === "Preferred Day and Time for class")
        .map((item: any, index: number) => (
          <DescriptionList
            key={index}
            items={[{ label: "Preferred time (student timezone)", value: item.option }]}
          />
        ))}

      {/* Step 1: classes per week (onboarding v3 — no Fixed/Flexible slot type) */}
      <div className="find-teacher__modes">
        <div className="find-teacher__mode">
          <span className="ds-field__label">Classes per week</span>
          <Tabs
            ariaLabel="Classes per week"
            value={formData.classesPerWeek}
            items={[
              { id: "1", label: "1 per week" },
              { id: "2", label: "2 per week" },
            ]}
            onChange={(id) => {
              handleInputChange("classesPerWeek", id);
              handleInputChange("selectedDay2", "");
              handleInputChange("timeBucketId", "");
              setTimeBuckets([]);
            }}
          />
        </div>
      </div>

      {showSearchFields && (
        <FormGrid min={240}>
          <Field label="Course" required>
            {(ctl) => (
              <Select
                inputId={ctl.id}
                aria-describedby={ctl["aria-describedby"]}
                options={academic2CourseOptions}
                placeholder="Please Select Course"
                value={academic2CourseOptions.find((opt) => opt.value === selectedCourse)}
                onChange={(option) => {
                  handleCourseChange(option);
                  setSelectedCourse(option?.value || "");
                  handleInputChange("timeBucketId", "");
                  setTimeBuckets([]);
                }}
                isDisabled={disableCourseSelect}
                {...selectMenuProps}
              />
            )}
          </Field>

          <Field label="Level" required>
            {(ctl) => (
              <Select
                inputId={ctl.id}
                aria-describedby={ctl["aria-describedby"]}
                options={levelOptions}
                placeholder="Select Level"
                value={levelOptions.find(opt => opt.value === (isKaraokeSelected ? 'Preparatory' : formData.level))}
                onChange={(selectedOption: any) => !isKaraokeSelected && handleInputChange('level', selectedOption?.value)}
                isDisabled={isKaraokeSelected}
                {...selectMenuProps}
              />
            )}
          </Field>

          <Field label="Starts in how many days?" required>
            {(ctl) => (
              <Select
                inputId={ctl.id}
                aria-describedby={ctl["aria-describedby"]}
                options={weeksCountOptions}
                value={weeksCountOptions.find((opt:any) => opt.value === formData.selectDaysCount) || null}
                onChange={(selected) => handleInputChange('selectDaysCount', selected?.value || "")}
                placeholder="Select Days Count"
                {...selectMenuProps}
              />
            )}
          </Field>

          <Field label="Preferred day" required>
            {(ctl) => (
              <Select
                inputId={ctl.id}
                options={dayOptions.filter((d) => d.value !== formData.selectedDay2)}
                placeholder="Select Day"
                value={dayOptions.find((d) => d.value === formData.selectedDay1) || null}
                onChange={(selectedOption: any) =>
                  handleInputChange("selectedDay1", selectedOption?.value || "")
                }
                {...selectMenuProps}
              />
            )}
          </Field>

          {preferredDayCount === 2 && (
            <Field label="Preferred day 2" required>
              {(ctl) => (
                <Select
                  inputId={ctl.id}
                  options={dayOptions.filter((d) => d.value !== formData.selectedDay1)}
                  placeholder="Select Day"
                  value={dayOptions.find((d) => d.value === formData.selectedDay2) || null}
                  onChange={(selectedOption: any) =>
                    handleInputChange("selectedDay2", selectedOption?.value || "")
                  }
                  {...selectMenuProps}
                />
              )}
            </Field>
          )}

          <Field
            label="Preferred time of day"
            required
            hint={
              bucketsLoading
                ? "Loading morning / afternoon / evening options…"
                : !bucketsLoading &&
                    formData.courseId &&
                    formData.selectDaysCount &&
                    selectedDaysList().length >= preferredDayCount &&
                    timeBuckets.length === 0
                  ? "No Teacher Found"
                  : undefined
            }
          >
            {(ctl) => (
              <Select
                inputId={ctl.id}
                isDisabled={bucketsLoading || timeBuckets.length === 0}
                options={timeBuckets.map((b) => {
                  const isFull = String(b.status || "").toUpperCase() !== "AVAILABLE";
                  const baseLabel = `${b.dayPartLabel || b.dayPart || "Slot"}${b.label ? ` · ${b.label}` : ""}`;
                  return {
                    value: String(b.id),
                    label: isFull ? `${baseLabel} — Full` : baseLabel,
                    isDisabled: isFull,
                  };
                })}
                placeholder={
                  bucketsLoading
                    ? "Loading…"
                    : timeBuckets.length
                      ? "Select Morning / Afternoon / Evening / Night"
                      : formData.courseId &&
                          formData.selectDaysCount &&
                          selectedDaysList().length >= preferredDayCount
                        ? "No Teacher Found"
                        : "Select preferred days first"
                }
                value={
                  timeBuckets
                    .map((b) => ({ value: String(b.id), label: b.dayPartLabel || b.dayPart || String(b.id) }))
                    .find((o) => o.value === formData.timeBucketId) || null
                }
                onChange={(selectedOption: any) =>
                  handleInputChange("timeBucketId", selectedOption?.value || "")
                }
                {...selectMenuProps}
              />
            )}
          </Field>

          <Field label="Gender">
            {(ctl) => (
              <Select
                inputId={ctl.id}
                options={genderOptions}
                value={genderOptions.find(opt => opt.value === formData.sgender)}
                onChange={(selected) => handleInputChange('sgender', selected?.value || null)}
                isClearable
                placeholder="Select Gender"
                {...selectMenuProps}
              />
            )}
          </Field>

          <Field label="Language">
            {(ctl) => (
              <Select
                inputId={ctl.id}
                options={languageOptions}
                value={languageOptions.find((opt:any) => opt.value === formData.studentLanguageId)}
                onChange={(selected: any) => handleInputChange('studentLanguageId', selected?.value || null)}
                isClearable
                placeholder="Select Language"
                {...selectMenuProps}
              />
            )}
          </Field>

          <Field label="Age group">
            {(ctl) => (
              <Select
                inputId={ctl.id}
                options={ageGroupOptions}
                value={ageGroupOptions.find((opt:any) => opt.value === formData.ageGroupId)}
                onChange={(selected: any) => handleInputChange('ageGroupId', selected?.value || null)}
                isClearable
                placeholder="Select Age Group"
                {...selectMenuProps}
              />
            )}
          </Field>
        </FormGrid>
      )}

      {showSearchFields && (
        <Actions>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!isFormValid()}
            loading={isLoading}
          >
            {isLoading ? 'Loading...' : 'Submit'}
          </Button>
        </Actions>
      )}

      {/* Teacher results, grouped by payout tier */}
      {tableData.length > 0 && (
        <div ref={resultsRef}>
          <Card
            title="Teacher Data"
            subtitle={`Pick one time per day, then Book All Classes. Teachers with more than ${TEACHER_CAPACITY_LIMIT} active students are at capacity.`}
          >
            {groupedTeacherData.map(({ tier, teachers }) => {
              const isExpanded = expandedTier === tier;
              const availableCount = teachers.filter(
                (teacher) => !isTeacherAtCapacity(teacher)
              ).length;
              const capacityCount = teachers.length - availableCount;
              const tierLabel = `${tier.charAt(0)}${tier.slice(1).toLowerCase()}`;
              const tierTone = TIER_TONES[tier] ?? "neutral";

              return (
                <Disclosure
                  key={tier}
                  open={isExpanded}
                  onToggle={() => setExpandedTier(isExpanded ? "" : tier)}
                  summary={
                    <span className="find-teacher__tier-summary">
                      <Badge tone={tierTone}>{tierLabel}</Badge>
                      <span className="ds-text-muted">
                        {availableCount} available
                        {capacityCount > 0 ? ` · ${capacityCount} at capacity` : ""}
                      </span>
                    </span>
                  }
                >
                  <Table caption={`${tierLabel} teachers`}>
                    <thead>
                      <tr>
                        <th>Teacher</th>
                        <th>Available slots</th>
                        <th className="ds-num">Active</th>
                        <th className="ds-num">Total</th>
                        {onTeacherSelect && <th>Select</th>}
                        {showBookAllClasses && <th>Book</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {teachers.map((teacher, index) => {
                        const activeStudents = getTeacherActiveStudents(teacher);
                        const atCapacity = isTeacherAtCapacity(teacher);
                        const teacherKey =
                          teacher.teacherId ?? teacher.id ?? `${tier}-${index}`;
                        const booking =
                          bookingTeacherId === (teacher.teacherId ?? teacher.id);
                        const selectedByDay =
                          slotSelections[teacherSelectionKey(teacher)] || {};
                        const needSlots = preferredDayCount || 1;
                        const hasEnoughSlots =
                          Object.keys(selectedByDay).length >= needSlots;

                        return (
                          <tr
                            key={teacherKey}
                            className={atCapacity ? "find-teacher__row--capacity" : ""}
                          >
                            <td>
                              <div className="find-teacher__teacher">
                                <span className="find-teacher__name">
                                  <span className="find-teacher__teacher-name">{`${teacher.firstName} ${teacher.lastName}`}</span>
                                  {teacher.allocationPriority === "1" && (
                                    <Badge tone="primary">Priority</Badge>
                                  )}
                                  {atCapacity && <Badge tone="danger">At capacity</Badge>}
                                </span>
                                <span className="find-teacher__teacher-meta">
                                  <Badge tone={tierTone}>{tierLabel}</Badge>
                                  {teacher.timezone ? (
                                    <span className="ds-text-subtle">{teacher.timezone}</span>
                                  ) : null}
                                  <Link
                                    to={`/pages/teacher-profile/${teacher.credentialId}/${teacher.teacherId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="find-teacher__profile-link"
                                  >
                                    Profile
                                  </Link>
                                </span>
                              </div>
                            </td>
                            <td>
                              <TeacherAvailabilityCell
                                teacher={teacher}
                                selectable={showBookAllClasses && !atCapacity}
                                selectedByDay={selectedByDay}
                                requiredCount={needSlots}
                                disabled={atCapacity || booking}
                                onToggleTime={(day, time) =>
                                  toggleTeacherSlot(teacher, day, time)
                                }
                              />
                            </td>
                            <td
                              className={
                                atCapacity ? "ds-num ds-strong find-teacher__over" : "ds-num"
                              }
                            >
                              {activeStudents}
                            </td>
                            <td className="ds-num">{teacher.paidStudents ?? "—"}</td>

                            {onTeacherSelect && (
                              <td>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  disabled={atCapacity}
                                  title={
                                    atCapacity
                                      ? `At capacity: ${activeStudents} active students`
                                      : undefined
                                  }
                                  onClick={() => {
                                    if (!atCapacity) onTeacherSelect(teacher);
                                  }}
                                >
                                  Select
                                </Button>
                              </td>
                            )}
                            {showBookAllClasses && (
                                <td>
                                  <Button
                                    variant="accent"
                                    size="sm"
                                    className="find-teacher__nowrap"
                                    disabled={atCapacity || !hasEnoughSlots}
                                    loading={booking}
                                    title={
                                      atCapacity
                                        ? `At capacity: ${activeStudents} active students`
                                        : !hasEnoughSlots
                                          ? `Select ${needSlots} time${needSlots === 1 ? "" : "s"} first`
                                          : undefined
                                    }
                                    onClick={() => void handleBookAllClasses(teacher)}
                                  >
                                    {booking ? "Loading..." : "Book All Classes"}
                                  </Button>
                                </td>
                              )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Disclosure>
              );
            })}
          </Card>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default FindTeacher;
